import { RevenuePanel } from "@/components/RevenuePanel";
import { PartnerCouponsPanel } from "@/components/PartnerCouponsPanel";
import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Archive, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHead } from "@/pages/HistoryPage";
import { PigmentRow, usePigmentFilter } from "@/pages/LibraryPage";
import { PigmentDialog } from "@/components/PigmentDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { LegalEditor } from "@/components/LegalEditor";
import { CiReview } from "@/components/CiReview";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7] p-5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A88B7D]">{label}</p>
      <p className="mt-1 font-serif text-4xl font-semibold text-[#2C1810]" data-testid={testId}>{value ?? "—"}</p>
    </div>
  );
}

function Records() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [toRetire, setToRetire] = useState(null);
  const { data = [] } = useQuery({ queryKey: ["pigments", "all"], queryFn: async () => (await api.get("/pigments?include_retired=true")).data });
  const { list, controls } = usePigmentFilter(data);
  const refresh = () => qc.invalidateQueries({ queryKey: ["pigments"] });
  const retire = async (p) => {
    try { await api.delete(`/admin/pigments/${p.id}`); refresh(); toast.success("Record retired"); } catch (e) { toast.error(errMsg(e)); }
  };
  const btn = "rounded-full p-1.5 text-[#6E5549] hover:bg-[#F6F1EA] hover:text-[#2C1810]";
  return (
    <div>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex-1">{controls}</div>
        <button data-testid="admin-add-pigment-btn" onClick={() => { setEdit(null); setOpen(true); }} className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#2C1810] px-5 text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317]"><Plus size={16} /> New record</button>
      </div>
      <p className="mt-4 text-xs text-[#A88B7D]">{list.length} {list.length === 1 ? "record" : "records"} (including retired)</p>
      <div className="mt-3 overflow-hidden rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7]" data-testid="admin-pigment-list">
        {list.map((p) => (
          <PigmentRow key={p.id} p={p} actions={<>
            <button className={btn} data-testid={`admin-edit-${p.id}`} onClick={() => { setEdit(p); setOpen(true); }} aria-label="Edit"><Pencil size={14} /></button>
            {p.status !== "retired" && <button className={btn} data-testid={`admin-retire-${p.id}`} onClick={() => setToRetire(p)} aria-label="Retire"><Archive size={14} /></button>}
          </>} />
        ))}
      </div>
      <PigmentDialog open={open} onOpenChange={setOpen} pigment={edit} onSaved={refresh} />
      <ConfirmDialog open={!!toRetire} onOpenChange={(o) => !o && setToRetire(null)} testId="admin-retire-dialog"
        title="Retire this record?" description={toRetire ? `${toRetire.brand} · ${toRetire.shade || toRetire.line} will be hidden from artists and from Ink Genius. You can reactivate it by editing its status.` : ""}
        confirmLabel="Retire" onConfirm={() => retire(toRetire)} />
    </div>
  );
}

function CsvImport({ imports }) {
  const qc = useQueryClient();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/pigments/import", fd);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`);
      qc.invalidateQueries({ queryKey: ["pigments"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-dashed border-[#D99B90] bg-[#FDF2F0] p-8">
        <h3 className="text-2xl font-semibold text-[#2C1810]">Import a CSV</h3>
        <p className="mt-2 text-sm leading-relaxed text-[#6E5549]">Recognised columns: brand (required), manufacturer, line, shade, aliases, region, batch, revision_date, inci, ci_numbers / CI, source_url, sds_url, source_note / notes, verification_status, technique_notes, status, last_reviewed. Records are matched on brand + line + shade; the original file name and import date are kept on each record.</p>
        <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={onFile} data-testid="admin-csv-input" />
        <button onClick={() => input.current?.click()} disabled={busy} data-testid="admin-csv-upload-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2C1810] px-6 py-3 text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:opacity-50"><Upload size={16} /> {busy ? "Importing…" : "Choose CSV file"}</button>
      </div>
      <div className="rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7] p-8" data-testid="admin-import-history">
        <h3 className="text-2xl font-semibold text-[#2C1810]">Import history</h3>
        <ul className="mt-4 divide-y divide-[#E6DBCF] text-sm">
          {(imports || []).map((i) => (
            <li key={`${i.filename}-${i.imported_at}`} className="py-2.5"><p className="font-medium text-[#2C1810]">{i.filename}</p><p className="text-xs text-[#A88B7D]">{i.imported_at.slice(0, 10)} · {i.created} created · {i.updated} updated · {i.skipped} skipped</p></li>
          ))}
          {!imports?.length && <li className="py-3 text-xs italic text-[#A88B7D]">No imports yet.</li>}
        </ul>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { data: s } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });
  return (
    <div className="mx-auto max-w-6xl px-6 py-10 sm:px-10 lg:py-14">
      <PageHead eyebrow="Owner" title="Knowledge & usage" sub="Maintain the authoritative pigment dataset. Only the owner decides which records count as verified." />
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-5">
        <Stat label="Artists" value={s?.artists} testId="admin-stat-artists" />
        <Stat label="Consultations" value={s?.consultations} testId="admin-stat-consultations" />
        <Stat label="Kit items" value={s?.kit_items} testId="admin-stat-kit" />
        <Stat label="Active records" value={s?.pigments} testId="admin-stat-pigments" />
        <Stat label="Verified shades" value={s?.pigments_by_status?.verified_shade} testId="admin-stat-verified" />
      </div>
      <Tabs defaultValue="records" className="mt-10">
        <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-3xl bg-[#F6F1EA] p-1 sm:rounded-full">
          <TabsTrigger value="records" data-testid="admin-tab-records" className="rounded-full">Pigment records</TabsTrigger>
          <TabsTrigger value="ci" data-testid="admin-tab-ci-review" className="rounded-full">CI review</TabsTrigger>
          <TabsTrigger value="import" data-testid="admin-tab-import" className="rounded-full">CSV import</TabsTrigger>
          <TabsTrigger value="legal" data-testid="admin-tab-legal" className="rounded-full">Legal pages</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="admin-tab-revenue" className="rounded-full">Revenue</TabsTrigger>
          <TabsTrigger value="partners" data-testid="admin-tab-partners" className="rounded-full">Partner Access & Coupons</TabsTrigger>
        </TabsList>
        <TabsContent value="records" className="mt-6"><Records /></TabsContent>
        <TabsContent value="ci" className="mt-6"><CiReview /></TabsContent>
        <TabsContent value="import" className="mt-6"><CsvImport imports={s?.imports} /></TabsContent>
        <TabsContent value="legal" className="mt-6"><LegalEditor /></TabsContent>
        <TabsContent value="revenue" className="mt-6"><RevenuePanel /></TabsContent>
        <TabsContent value="partners" className="mt-6"><PartnerCouponsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
